// This file will contain centralized logic for external API calls, like OCR functions.
import { supabase } from '@/integrations/supabase/client';

// API Response Types
export interface OcrResponse {
  text: string;
  confidence?: number;
  error?: string;
}

export interface LabelExtractionResponse {
  guest_name?: string;
  tracking_number?: string;
  room_number?: string;
  error?: string;
}

export interface ApiError extends Error {
  status?: number;
  code?: string;
}

// Error handling utility
const createApiError = (message: string, status?: number): ApiError => {
  const error = new Error(message) as ApiError;
  error.status = status;
  return error;
};

/**
 * A generic fetch utility for calling Supabase Edge Functions or other APIs.
 * It automatically includes the Authorization header if the user is logged in.
 *
 * @param url The URL to fetch.
 * @param options The fetch options (method, body, etc.).
 * @returns The JSON response from the API.
 */
export const apiFetch = async <T = unknown>(url: string, options: RequestInit = {}): Promise<T> => {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = new Headers(options.headers || {});
  
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      switch (response.status) {
        case 401:
          errorMessage = "Authentication error. Please log in again.";
          break;
        case 403:
          errorMessage = "You are not authorized to perform this action.";
          break;
        case 404:
          errorMessage = "The requested resource was not found.";
          break;
        case 500:
          errorMessage = "A server error occurred. Please try again later.";
          break;
      }
      throw createApiError(errorMessage, response.status);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return response.json() as Promise<T>;
    }

    return response as unknown as T;
  } catch (error: unknown) {
    if (error instanceof Error && error.message.includes("Failed to fetch")) {
        throw createApiError("Network error. Please check your connection and try again.");
    }
    throw error;
  }
};

/**
 * Sends an image URL to the OCR function and returns the extracted text.
 * @param imageUrl - The public URL of the image to process.
 * @returns The raw text extracted from the image.
 */
export const runOcr = async (imageUrl: string): Promise<OcrResponse> => {
  const ocrFunctionUrl = import.meta.env.VITE_OCR_FUNCTION_URL;
  if (!ocrFunctionUrl) {
    throw createApiError('OCR function URL is not configured');
  }

  const result = await apiFetch<OcrResponse>(ocrFunctionUrl, {
    method: 'POST',
    body: JSON.stringify({ imageUrl }),
  });

  if (!result.text) {
    throw createApiError('Could not extract text from image. Try retaking the photo.');
  }

  return result;
};

/**
 * Calls the openai-extract Edge Function to get structured data from OCR text.
 * @param ocrText - The OCR text to process.
 * @returns The extracted fields (guest_name, tracking_number).
 */
export const extractLabelFields = async (ocrText: string): Promise<LabelExtractionResponse> => {
    const extractFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-extract`;
    const result = await apiFetch<LabelExtractionResponse>(extractFunctionUrl, {
        method: 'POST',
        body: JSON.stringify({ ocrText }),
    });
    return result;
}; 